import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play, X } from "lucide-react";

import { DEMO_SCREENS } from "../lib/demoContent";
import { ScreenOpen } from "./demo/ScreenOpen";
import { ScreenAssumption } from "./demo/ScreenAssumption";
import { ScreenContext } from "./demo/ScreenContext";
import { ScreenQuestions } from "./demo/ScreenQuestions";
import { ScreenFlow } from "./demo/ScreenFlow";
import { ScreenResolution } from "./demo/ScreenResolution";

const SCREEN_COMPONENTS = {
  open: ScreenOpen,
  assumption: ScreenAssumption,
  context: ScreenContext,
  questions: ScreenQuestions,
  moment: ScreenFlow,
  resolution: ScreenResolution,
};

const Stepper = ({ screens, idx, onJump }) => (
  <div className="flex items-center gap-1" data-testid="demo-stepper">
    {screens.map((s, i) => {
      const active = i === idx;
      const done = i < idx;
      return (
        <button
          key={s.key}
          type="button"
          onClick={() => onJump(i)}
          data-testid={`demo-step-${i}`}
          aria-current={active ? "step" : undefined}
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full transition-colors text-xs ${
            active
              ? "bg-[#C4521E] text-[#F9F7F3]"
              : done
              ? "text-[#1A1814] hover:text-[#C4521E]"
              : "text-[#56534A] hover:text-[#1A1814]"
          }`}
        >
          <span
            className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${
              active
                ? "bg-[#F9F7F3]/20 text-[#F9F7F3]"
                : done
                ? "border border-[#C4521E] text-[#C4521E]"
                : "border border-[#DDD9CF] text-[#56534A]"
            }`}
          >
            {i + 1}
          </span>
          <span className="hidden lg:inline">{s.title}</span>
        </button>
      );
    })}
  </div>
);

/** Progress bar at the bottom of the screen — fills over the scene's duration. */
const ProgressBar = ({ durationMs, sceneKey, playing }) => (
  <div className="h-[2px] bg-[#DDD9CF] w-full overflow-hidden">
    {playing && (
      <motion.div
        key={sceneKey}
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{ duration: durationMs / 1000, ease: "linear" }}
        className="h-full bg-[#C4521E]"
      />
    )}
  </div>
);

export const DemoPlayer = ({ onExit }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true); // autoplay ON — this is a story, let it tell itself
  const screens = DEMO_SCREENS;
  const currentScreen = screens[idx];

  // Per-scene autoplay tick.
  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => {
      setIdx((i) => {
        if (i + 1 >= screens.length) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, currentScreen.durationMs);
    return () => clearTimeout(t);
  }, [idx, playing, screens.length, currentScreen.durationMs]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onExit?.();
      if (e.key === "ArrowRight") {
        setPlaying(false);
        setIdx((i) => Math.min(screens.length - 1, i + 1));
      }
      if (e.key === "ArrowLeft") {
        setPlaying(false);
        setIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit, screens.length]);

  const ScreenComponent = SCREEN_COMPONENTS[currentScreen.key];

  const goPrev = () => {
    setPlaying(false);
    setIdx((i) => Math.max(0, i - 1));
  };
  const goNext = () => {
    setPlaying(false);
    setIdx((i) => Math.min(screens.length - 1, i + 1));
  };
  const jump = (i) => {
    setPlaying(false);
    setIdx(i);
  };

  const isLast = idx === screens.length - 1;

  return (
    <div className="fixed inset-0 z-40 bg-[#F9F7F3] overflow-hidden flex flex-col" data-testid="demo-player">
      {/* Top control bar */}
      <div className="border-b border-[#DDD9CF] bg-[#F9F7F3]">
        <div className="max-w-5xl xl:max-w-6xl mx-auto px-6 xl:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#C4521E]">A story</div>
            <span className="text-[#DDD9CF]">·</span>
            <Stepper screens={screens} idx={idx} onJump={jump} />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border border-[#DDD9CF] rounded-full">
              <button
                type="button"
                onClick={goPrev}
                disabled={idx === 0}
                className="w-8 h-8 flex items-center justify-center text-[#56534A] hover:text-[#C4521E] disabled:opacity-30"
                aria-label="Previous scene"
                data-testid="demo-prev"
              >
                <ChevronLeft size={14} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className={`w-8 h-8 flex items-center justify-center transition-colors ${
                  playing ? "text-[#C4521E]" : "text-[#56534A] hover:text-[#C4521E]"
                }`}
                aria-label={playing ? "Pause" : "Play"}
                title={playing ? "Pause (Space)" : "Play (Space)"}
                data-testid="demo-playpause"
              >
                {playing ? <Pause size={13} strokeWidth={1.5} /> : <Play size={13} strokeWidth={1.5} />}
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={isLast}
                className="w-8 h-8 flex items-center justify-center text-[#56534A] hover:text-[#C4521E] disabled:opacity-30"
                aria-label="Next scene"
                data-testid="demo-next"
              >
                <ChevronRight size={14} strokeWidth={1.5} />
              </button>
            </div>

            <button
              type="button"
              onClick={onExit}
              className="text-xs text-[#56534A] hover:text-[#C4521E] flex items-center gap-1"
              data-testid="demo-skip"
              title="Press Esc to exit"
            >
              Skip <X size={12} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <ProgressBar durationMs={currentScreen.durationMs} sceneKey={currentScreen.key} playing={playing} />
      </div>

      {/* Scene body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl xl:max-w-6xl mx-auto px-6 xl:px-10 py-12 xl:py-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentScreen.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <ScreenComponent />
            </motion.div>
          </AnimatePresence>

          {isLast && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 4.6 }}
              className="mt-12 flex flex-col sm:flex-row items-center gap-4"
              data-testid="demo-finished"
            >
              <button
                type="button"
                onClick={onExit}
                className="bg-[#C4521E] text-[#F9F7F3] px-8 py-3 font-medium tracking-wide hover:bg-[#a34418] transition-colors text-sm"
                data-testid="demo-try-it"
              >
                Try it with your own product →
              </button>
              <button
                type="button"
                onClick={() => {
                  setIdx(0);
                  setPlaying(true);
                }}
                className="text-sm text-[#56534A] hover:text-[#C4521E]"
                data-testid="demo-replay"
              >
                Replay the story
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
